import React, { forwardRef, ForwardedRef } from 'react';
import { Box } from '@mui/material';
import { convertToRaw } from 'draft-js';
import MUIRichTextEditor from 'mui-rte';
import { stateFromHTML } from 'draft-js-import-html';
import { stateToHTML } from 'draft-js-export-html';

import Emoticons from '../../../../Utils/Emoticons';

const DEFAULT_OUTLINED_INPUT_HORIZONTAL_PADDING = 14;
const TEXTAREA_MIN_HEIGHT = 200;

const Wysiwyg = forwardRef(
  (
    {
      value = '',
      placeholder,
      onChange,
      id,
      minHeight = 0,
      editingOptions = [
        'title',
        'bold',
        'italic',
        'strikethrough',
        'undo',
        'redo',
        'link',
        'numberList',
        'bulletList',
      ],
    }: any,
    ref: ForwardedRef<any>,
  ): any => {
    const loadContent = (val: string) => {
      // Create the ContentState object
      const state = stateFromHTML(val);

      // Stringify `state` object from a Draft.Model.Encoding.RawDraftContentState object
      const stringifiedContent = JSON.stringify(convertToRaw(state));

      return stringifiedContent;
    };

    const [editorValue, setEditorValue] = React.useState(loadContent(value));
    const [editorHtmlValue, setEditorHtmlValue] = React.useState(value);

    // In case the value is not set before rendering the component, we get the value from the form
    // This is the case when an Idea with `form_submissions_attributes` or `post_record_submissions_attributes`
    // is being edited and the form is not fully loaded yet
    // if the value is updated from outside the component, not from the editor itself
    React.useEffect(() => {
      if (value !== editorHtmlValue) {
        const content = loadContent(value);

        setEditorValue(content);
      }
    }, [value]);

    const handleChange = (newState: any) => {
      const content = newState.getCurrentContent();

      if (!content.hasText()) {
        // This fixes the issue where if the input is cleared, we still have an empty node on it
        // by default it gets transformed to a paraphraph with a single line `<p><br/></p>`
        // we override it to return an empty string instead
        onChange('');
        setEditorHtmlValue('');
      } else {
        const html = stateToHTML(content);
        onChange(html);
        setEditorHtmlValue(html);
      }
    };

    return (
      <Box
        sx={{
          padding: (theme) =>
            `0 ${DEFAULT_OUTLINED_INPUT_HORIZONTAL_PADDING}px ${theme.spacing(1)}`,
          width: '100%',
          minHeight: minHeight || TEXTAREA_MIN_HEIGHT,
          overflow: 'hidden',
        }}
        onClick={() => {
          ref?.current?.focus();
        }}
      >
        <MUIRichTextEditor
          ref={ref}
          id={id}
          onChange={handleChange}
          defaultValue={editorValue}
          controls={editingOptions}
          autocomplete={{
            strategies: [
              {
                items: Emoticons,
                triggerChar: ':',
              },
            ],
          }}
          inlineToolbar
          inlineToolbarControls={editingOptions}
          label={placeholder}
          draftEditorProps={{ spellCheck: true }}
        />
      </Box>
    );
  },
);

Wysiwyg.displayName = 'Wysiwyg';

export default Wysiwyg;
